import { Check } from "lucide-react";

const services = [
  {
    n: "01",
    title: "Operational AI Security Assessment",
    tagline:
      "Identify what is exposed, misconfigured, over-permissioned, or vulnerable.",
    desc:
      "For organizations deploying AI across applications, agents, vendor tools, internal workflows, customer-facing systems, and business-critical operations. We assess the full surface, model, application, data, tools, permissions, approvals, and downstream business impact.",
    outcomesLabel: "Business outcomes",
    outcomes: [
      "A clear, prioritized picture of AI risk across the business",
      "Reduced exposure before external scrutiny",
      "An executive-ready view of what must be fixed and why",
    ],
    items: [
      "AI and application risk findings",
      "Agent and workflow exposure map",
      "Prioritized remediation roadmap",
      "Executive risk brief",
    ],
    cta: "Discuss an Assessment",
    primary: true,
  },
  {
    n: "02",
    title: "AI Risk Remediation & Controls",
    tagline:
      "Close the highest-risk gaps before AI risk becomes a business problem.",
    desc:
      "We move from findings to fixed. Practical remediation, control design, and secure workflow changes, owned end to end with your team.",
    outcomesLabel: "Business outcomes",
    outcomes: [
      "Reduced exposure on the highest-impact AI risks",
      "Working controls, not documented intentions",
      "Progress leadership can report with confidence",
    ],
    items: [
      "Remediation workplan",
      "Control design guidance",
      "Access and permission corrections",
      "Secure workflow changes",
      "Progress reporting for leadership",
    ],
    cta: "Discuss Remediation",
  },
  {
    n: "03",
    title: "Validation & Evidence Package",
    tagline:
      "Prove the fixes are working, with evidence that holds up under review.",
    desc:
      "We test the fixes, verify the controls, and produce documentation suitable for external and leadership review.",
    outcomesLabel: "Business outcomes",
    outcomes: [
      "Validated proof that risk has been reduced",
      "Cleaner buyer, audit, and insurance reviews",
      "Stronger leadership and board confidence",
    ],
    items: [
      "Fix validation results",
      "Control validation log",
      "Evidence package",
      "Review-ready documentation",
      "Executive validation brief",
    ],
    cta: "Discuss Validation",
  },
];

export default function ArqServices() {
  return (
    <section id="services" className="bg-arq-bg py-20 md:py-[120px] px-6">
      <div className="max-w-[1200px] mx-auto">
        <div className="text-center max-w-[860px] mx-auto mb-16">
          <div className="arq-label mb-6">Engagements</div>
          <h2 className="font-display font-semibold text-arq-text mb-5" style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}>
            Assess. Remediate. Validate.
          </h2>
          <p className="text-arq-muted text-[17px] leading-[1.75]">
            Three disciplined engagements built around business outcomes, not hours, not generic
            advisory. Each one is fixed-scope, evidence-driven, and designed to move production AI
            from exposed to defensible.
          </p>
        </div>

        {/* Primary - Assessment */}
        <div className="mb-10">
          <div className="arq-card p-8 md:p-10 flex flex-col border-arq-blue/40 ring-1 ring-arq-blue/30 shadow-[0_0_40px_-12px_hsl(var(--arq-blue)/0.35)] relative">
            <div className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-arq-blue text-white text-[11px] font-semibold uppercase tracking-[0.08em] shadow-md">
              Start Here
            </div>
            <div className="font-mono-tech text-[12px] text-arq-blue mb-3 tracking-[0.08em]">{services[0].n}</div>
            <h3 className="font-display text-[22px] md:text-[24px] font-semibold text-arq-text mb-2">{services[0].title}</h3>
            <p className="text-arq-teal text-[14px] font-medium italic mb-4">{services[0].tagline}</p>
            <p className="text-arq-muted text-[15px] leading-[1.7] mb-6 max-w-[860px]">{services[0].desc}</p>

            <div className="grid md:grid-cols-2 gap-8 mb-6">
              <div>
                <div className="font-mono-tech text-[11px] tracking-[0.1em] uppercase text-arq-tertiary mb-3">
                  {services[0].outcomesLabel}
                </div>
                <ul className="space-y-2.5">
                  {services[0].outcomes.map((o) => (
                    <li key={o} className="flex items-start gap-3 text-[14.5px] text-arq-text/90 leading-snug">
                      <Check className="w-4 h-4 mt-1 flex-shrink-0 text-arq-teal" aria-hidden />
                      <span>{o}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="font-mono-tech text-[11px] tracking-[0.1em] uppercase text-arq-tertiary mb-3">
                  Deliverables
                </div>
                <ul className="space-y-2.5">
                  {services[0].items.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-[14.5px] text-arq-text/90 leading-snug">
                      <Check className="w-4 h-4 mt-1 flex-shrink-0 text-arq-teal" aria-hidden />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex items-center justify-between gap-4 pt-5 border-t border-arq-border">
              <div className="font-mono-tech text-[12px] text-arq-tertiary">
                Fixed scope · Written proposal within 48 hours
              </div>
              <button
                onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
                className="text-arq-teal text-[13px] font-medium hover:text-arq-blue underline underline-offset-4 transition-colors whitespace-nowrap"
              >
                {services[0].cta} →
              </button>
            </div>
          </div>
        </div>

        <div className="text-center mb-8">
          <div className="arq-label text-arq-tertiary">After the Assessment</div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {services.slice(1).map((s) => (
            <div key={s.n} className="arq-card p-7 flex flex-col">
              <div className="font-mono-tech text-[12px] text-arq-blue mb-3 tracking-[0.08em]">{s.n}</div>
              <h3 className="font-display text-[19px] font-semibold text-arq-text mb-2">{s.title}</h3>
              <p className="text-arq-teal text-[13.5px] font-medium italic mb-3">{s.tagline}</p>
              <p className="text-arq-muted text-[14.5px] leading-[1.7] mb-5">{s.desc}</p>

              <div className="mb-4">
                <div className="font-mono-tech text-[10.5px] tracking-[0.1em] uppercase text-arq-tertiary mb-2.5">
                  {s.outcomesLabel}
                </div>
                <ul className="space-y-2">
                  {s.outcomes.map((o) => (
                    <li key={o} className="flex items-start gap-2.5 text-[13.5px] text-arq-text/90 leading-snug">
                      <Check className="w-3.5 h-3.5 mt-1 flex-shrink-0 text-arq-teal" aria-hidden />
                      <span>{o}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mb-5 flex-1">
                <div className="font-mono-tech text-[10.5px] tracking-[0.1em] uppercase text-arq-tertiary mb-2.5">
                  Deliverables
                </div>
                <ul className="space-y-2">
                  {s.items.map((it) => (
                    <li key={it} className="flex items-start gap-2.5 text-[13.5px] text-arq-text/90 leading-snug">
                      <Check className="w-3.5 h-3.5 mt-1 flex-shrink-0 text-arq-teal" aria-hidden />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
                className="mt-auto text-left text-arq-teal text-[13px] font-medium hover:text-arq-blue underline underline-offset-4 transition-colors pt-4 border-t border-arq-border"
              >
                {s.cta} →
              </button>
            </div>
          ))}
        </div>

        {/* Optional retainer */}
        <div className="mt-10 arq-card p-7 md:p-8 border-arq-border/80">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="max-w-[780px]">
              <div className="font-mono-tech text-[11px] tracking-[0.1em] uppercase text-arq-tertiary mb-2">
                Optional Retainer
              </div>
              <h3 className="font-display text-[20px] font-semibold text-arq-text mb-2">
                Continuous AI Assurance
              </h3>
              <p className="text-arq-muted text-[14.5px] leading-[1.7]">
                Ongoing quarterly review, remediation support, evidence updates, and AI risk
                monitoring for organizations that need to keep production AI defensible over time.
              </p>
            </div>
            <button
              onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
              className="shrink-0 text-arq-teal text-[13px] font-medium hover:text-arq-blue underline underline-offset-4 transition-colors whitespace-nowrap"
            >
              Discuss the Retainer →
            </button>
          </div>
        </div>

        {/* Pricing philosophy */}
        <div className="mt-16 max-w-[900px] mx-auto arq-card p-8 md:p-10">
          <h3 className="font-display text-[22px] font-semibold text-arq-text mb-4">
            Fixed Scope. Clear Outcomes. Written Options.
          </h3>
          <div className="space-y-4 text-arq-muted text-[15.5px] leading-[1.75]">
            <p>ArqeraAI does not bill against an open hourly clock.</p>
            <p>Each engagement is scoped around the value of the outcome being created: risk reduced, exposure closed, controls validated, evidence produced, executive decisions clarified.</p>
            <p>After an initial readiness conversation, we provide a written proposal with clear objectives, scope, outcomes, timeline, and investment options. The investment is based on the importance of the business problem, not the hours required to solve it.</p>
          </div>
        </div>
      </div>
    </section>
  );
}
